/**
 * CrmSellers - Página de listagem de vendedores do CRM
 * 
 * Funcionalidades:
 * - Listagem de vendedores com busca e filtros
 * - Criar, editar e excluir vendedores
 * - Visualização de status ativo/inativo
 * - Taxa de comissão
 * - KPIs com estatísticas da equipe
 * 
 * @version 3.0.0 - Padronizado com layout de Clientes
 */

import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  UserCheck,
  Mail,
  Phone,
  Percent,
  Link2,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { useCrmSellers, CrmSeller } from '@/hooks/useCrmSellers';
import { SellerFormDialog } from '@/components/crm/sellers/SellerFormDialog';
import { usePermissions } from '@/hooks/usePermissions';

// ============================================================================
// INTERFACES - Definição de tipos para estatísticas
// ============================================================================

/** Interface para estatísticas de vendedores (KPIs) */
interface SellerStats {
  total_count: number;
  active_count: number;
  with_user_count: number;
  avg_commission: number;
}

// ============================================================================
// COMPONENTES AUXILIARES - Cards de métricas (padrão Clientes)
// ============================================================================

/** Props do componente MetricCard */
interface MetricCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  variant?: 'primary' | 'success' | 'info' | 'purple';
  suffix?: string;
}

/**
 * Estilos de variantes para KPI cards - padrão horizontal compacto
 */
const METRIC_CARD_STYLES: Record<string, { cardBg: string; iconColor: string }> = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/**
 * Card de métrica compacto horizontal - padrão unificado CRM
 */
function MetricCard({ title, value, icon: Icon, variant = 'primary', suffix }: MetricCardProps) {
  const styles = METRIC_CARD_STYLES[variant] || METRIC_CARD_STYLES.primary;

  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
            {value}{suffix}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL - Gerenciamento de vendedores do CRM
// ============================================================================

/**
 * Página principal de vendedores - Layout padronizado com Clientes
 */
export default function CrmSellers() {
  const { sellers, isLoading, deleteSeller } = useCrmSellers();

  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'crm_sellers';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  
  // Estados locais
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingSeller, setEditingSeller] = useState<CrmSeller | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sellerToDelete, setSellerToDelete] = useState<CrmSeller | null>(null);

  // ============================================================================
  // COMPUTED - Estatísticas calculadas a partir dos dados
  // ============================================================================

  /** Calcula estatísticas/KPIs dos vendedores */
  const stats = useMemo<SellerStats>(() => {
    const activeCount = sellers.filter(s => s.is_active).length;
    const withUserCount = sellers.filter(s => s.user_id).length;
    const avgCommission = sellers.length > 0
      ? sellers.reduce((acc, s) => acc + (s.commission_rate || 0), 0) / sellers.length
      : 0;

    return {
      total_count: sellers.length,
      active_count: activeCount,
      with_user_count: withUserCount,
      avg_commission: Math.round(avgCommission * 10) / 10,
    };
  }, [sellers]);

  /** Filtra vendedores pela busca e status */
  const filteredSellers = useMemo(() => {
    let result = sellers;
    
    // Filtro por status
    if (statusFilter === 'active') {
      result = result.filter(s => s.is_active);
    } else if (statusFilter === 'inactive') {
      result = result.filter(s => !s.is_active);
    }
    
    // Filtro por busca
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(seller => (
        seller.name.toLowerCase().includes(searchLower) ||
        seller.email?.toLowerCase().includes(searchLower) ||
        seller.phone?.includes(search)
      ));
    }
    
    return result;
  }, [sellers, search, statusFilter]);

  /** Verifica se há filtros ativos */
  const hasActiveFilters = search || statusFilter !== 'all';

  /** Limpa todos os filtros */
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
  };

  // ============================================================================
  // HANDLERS - Ações do usuário
  // ============================================================================

  /** Handler para abrir edição */
  const handleEdit = (seller: CrmSeller) => {
    setEditingSeller(seller);
    setFormOpen(true);
  };

  /** Handler para abrir confirmação de exclusão */
  const handleDeleteClick = (seller: CrmSeller) => {
    setSellerToDelete(seller);
    setDeleteDialogOpen(true);
  };

  /** Handler para confirmar exclusão */
  const handleDeleteConfirm = async () => {
    if (sellerToDelete) {
      await deleteSeller.mutateAsync(sellerToDelete.id);
      setDeleteDialogOpen(false);
      setSellerToDelete(null);
    }
  };

  /** Handler para fechar dialog de formulário */
  const handleFormClose = () => {
    setFormOpen(false);
    setEditingSeller(null);
  };

  /** Handler para sucesso no formulário */
  const handleFormSuccess = () => {
    handleFormClose();
  };

  // ============================================================================
  // RENDER - Interface principal (Layout padrão Clientes)
  // ============================================================================

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto - Padrão Clientes */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Vendedores</h1>
        </div>
        {hasCreatePermission && (
          <Button 
            size="xs"
            onClick={() => setFormOpen(true)}
            className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Novo Vendedor
          </Button>
        )}
      </div>

      {/* KPI Cards em Grid - Padrão Clientes */}
      {!isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          <MetricCard
            title="Total Vendedores"
            value={stats.total_count}
            icon={Users}
            variant="primary"
          />
          <MetricCard
            title="Ativos"
            value={stats.active_count}
            icon={UserCheck}
            variant="success"
          />
          <MetricCard
            title="Vinculados"
            value={stats.with_user_count}
            icon={Link2}
            variant="info"
          />
          <MetricCard
            title="Comissão Média"
            value={stats.avg_commission}
            icon={Percent}
            variant="purple"
            suffix="%"
          />
        </div>
      )}

      {/* Barra de Filtros Inline - Padrão Clientes */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, email ou telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Filtro Status */}
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive')}>
          <SelectTrigger className="w-[130px] h-8 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>

        {/* Botão Limpar */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs">
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {/* Indicador carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* Container da Tabela - Sem Card wrapper */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : filteredSellers.length === 0 ? (
          /* Estado vazio compacto - Padrão Clientes */
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <UserCheck className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">
                {search || statusFilter !== 'all' ? 'Nenhum vendedor encontrado' : 'Nenhum vendedor cadastrado'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {search || statusFilter !== 'all' ? 'Tente ajustar os filtros' : 'Cadastre seu primeiro vendedor'}
              </p>
            </div>
            {!search && statusFilter === 'all' && hasCreatePermission && (
              <Button size="xs" onClick={() => setFormOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Novo Vendedor
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs font-medium">Vendedor</TableHead>
                <TableHead className="text-xs font-medium">Contato</TableHead>
                <TableHead className="text-xs font-medium">Comissão</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSellers.map(seller => (
                <TableRow 
                  key={seller.id}
                  className="cursor-pointer hover:bg-muted/40 group"
                >
                  {/* Nome e Avatar */}
                  <TableCell className="py-2">
                    <div className="flex items-center gap-3">
                      <SmartAvatar
                        src={seller.user?.avatar_url || seller.avatar_url}
                        name={seller.name}
                        size="md"
                        use3DFallback={true}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{seller.name}</span>
                        {seller.user_id && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Link2 className="h-3 w-3" />
                            Vinculado ao sistema
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  {/* Contato */}
                  <TableCell className="py-2">
                    <div className="flex flex-col gap-0.5 text-sm">
                      {seller.email && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          <span className="text-xs">{seller.email}</span>
                        </div>
                      )}
                      {seller.phone && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          <span className="text-xs">{seller.phone}</span>
                        </div>
                      )}
                      {!seller.email && !seller.phone && (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </div>
                  </TableCell>

                  {/* Comissão */}
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1 text-sm">
                      <Percent className="h-3 w-3 text-muted-foreground" />
                      <span>{seller.commission_rate}%</span>
                    </div>
                  </TableCell>

                  {/* Status — Badge outline + StatusDot (padrão Departamentos) */}
                  <TableCell className="py-2">
                    <Badge
                      variant="outline"
                      className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                    >
                      <StatusDot tone={seller.is_active ? 'emerald' : 'muted'} />
                      {seller.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>

                  {/* Ações - Aparece no hover */}
                  <TableCell className="py-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {hasEditPermission && (
                          <DropdownMenuItem onClick={() => handleEdit(seller)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                        )}
                        {hasDeletePermission && (
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(seller)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Dialog de Formulário */}
      <SellerFormDialog
        open={formOpen}
        onOpenChange={handleFormClose}
        seller={editingSeller}
        onSuccess={handleFormSuccess}
      />

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir vendedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o vendedor "{sellerToDelete?.name}"?
              Esta ação não pode ser desfeita. Os negócios e contatos associados
              a este vendedor perderão a referência.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSeller.isPending ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
